<template>
  <section class="table-wrap">
    <ve-table
      :fixed-header="true"
      max-height="100%"
      :columns="columns"
      :table-data="tableData"
      :border-around="false"
      :sort-option="sortOption"
      :scroll-width="scrollWidth"
    />

    <empty-state v-if="showSearchEmptyState" :title="$t('CAMPAIGN.LIST.404')" />

    <empty-state
      v-else-if="!isLoading && !campaigns.length"
      :title="$t('CAMPAIGN.LIST.NO_CAMPAIGNS')"
    />

    <div v-if="isLoading" class="campaign--loader">
      <spinner />
      <span>{{ $t('CAMPAIGN.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<script>

import { mapGetters } from 'vuex';

import { VeTable } from 'vue-easytable';

import Spinner from 'shared/components/Spinner';
import EmptyState from 'dashboard/components/widgets/EmptyState';
import InboxName from 'dashboard/components/widgets/InboxName';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName';
import Label from 'dashboard/components/ui/Label';
import CampaignPublic from './CampaignPublic';

import { mixin as clickaway } from 'vue-clickaway';
import campaignMixin from 'shared/mixins/campaignMixin';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';

export default {
  components: {
    EmptyState,
    Spinner,
    VeTable,
  },
  mixins: [
    clickaway,
    timeMixin,
    rtlMixin,
    campaignMixin,
    messageFormatterMixin,
  ],
  props: {
    campaigns: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    onClickCampaign: {
      type: Function,
      default: () => {},
    },
    showSearchEmptyState: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortChange: params => this.$emit('on-sort-change', params),
      },
    };
  },
  computed: {
    ...mapGetters({
      isAccountTypeParent: 'isAccountTypeParent'
    }),
    tableData() {
      if (this.isLoading) {
        return [];
      }

      return this.campaigns;
    },
    // Sum of every column's own width so the table's total width always
    // matches its columns exactly: on wide screens ve-table stretches all
    // columns proportionally to fill the extra space (keeping their
    // relative proportions, so no single column ends up with a huge gap),
    // and on narrow screens it never shrinks a column below the width it
    // was actually given - it scrolls horizontally instead of squeezing
    // text (and its sort arrow) onto a second line.
    scrollWidth() {
      const total = this.columns.reduce(
        (sum, column) =>
          sum + (typeof column.width === 'number' ? column.width : 0),
        0
      );
      return `${total}px`;
    },
    columns() {
      const columns =  [
        {
          field: 'title',
          key: 'title',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.TITLE'),
          fixed: 'left',
          width: 260,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.title || '',
          renderBodyCell: ({ row }) => (
            <woot-button
              variant="clear"
              onClick={() => this.onClickCampaign(row.id)}
            >
              <div class="row--campaign-block">
                <div class="campaign-block">
                  <h6 class="sub-block-title text-truncate">{row.title}</h6>
                  <button class="button clear small link view-details--button">
                    {this.$t('CONTACTS_PAGE.LIST.VIEW_DETAILS')}
                  </button>
                </div>
              </div>
            </woot-button>
          ),
        },
        {
          field: 'inbox',
          key: 'inbox',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.INBOX'),
          width: 190,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.inbox || '',
          renderBodyCell: ({ row }) => {
            if(!row.inbox){
              row.inbox = {type: 'unknown', name: this.$t('CAMPAIGN.LIST.INBOX.404') };
            }
            return <InboxName inbox={row.inbox} />;
          },
        },
        {
          field: 'sender',
          key: 'sender',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.SENDER'),
          width: 190,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.sender || '',
          renderBodyCell: ({ row }) => {
            if(!row.sender){
              row.sender = {name: this.$t('CAMPAIGN.LIST.SENDER.404') };
            }
            return <UserAvatarWithName user={row.sender} />;
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.CREATED_AT'),
          width: 175,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.created_at || '',
          renderBodyCell: ({ row }) => (
            <span>
              {this.messageStamp(
                this.generateTimestamp(row.created_at),
                'dd/MM/yy - HH:mm'
              )}
            </span>
          ),
        },
        {
          field: 'scheduled_at',
          key: 'scheduled_at',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.SCHEDULED_AT'),
          width: 175,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.scheduled_at || '',
          renderBodyCell: ({ row }) => (
            <span>
              {this.messageStamp(
                new Date(row.scheduled_at),
                'dd/MM/yy - HH:mm'
              )}
            </span>
          ),
        },
        {
          field: 'audience',
          key: 'audience',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.PUBLIC'),
          width: 220,
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            if (row.audience_type === 'file') {
              return (<span>{this.$t('CAMPAIGN.ADD.FORM.IMPORT_FILE.LABEL')}</span>);
            }

            const audience = this.getLabels(row.audience.map(({ id }) => id));
            if(audience.length === 0){
              return (<span>{this.$t('CAMPAIGN.ADD.FORM.TYPE_AUDIENCE.TYPES.LABEL')}</span>);
            }

            return <CampaignPublic labels={audience} />;
          },
        },
        {
          field: 'campaign_status',
          key: 'campaign_status',
          title: this.$t('CAMPAIGN.LIST.TABLE_HEADER.STATUS'),
          width: 140,
          align: this.isRTLView ? 'right' : 'left',
          sortBy: this.sortConfig.campaign_status || '',
          renderBodyCell: ({ row }) => {
            const labelText =
              row.campaign_status === 'completed'
                ? this.$t('CAMPAIGN.LIST.STATUS.COMPLETED')
                : this.$t('CAMPAIGN.LIST.STATUS.ACTIVE');
            const colorScheme =
              row.campaign_status === 'completed' ? 'secondary' : 'success';
            return <Label title={labelText} colorScheme={colorScheme} />;
          },
        },
      ];
      if(this.isAccountTypeParent){
        return [...[{
          field: 'account',
          key: 'account',
          title: 'Conta',
          width: 140,
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => (
            row.account.status === 'active' ?
            <a 
              href={`/app/accounts/${row.account.id}/campaigns/whatsapp`}              
            >
              {row.account.name}
            </a>
            : <span>{row.account.name}</span>
          ),
        },], ...columns]

      }
      return columns;
    },
    emptyMessage() {
      return this.campaigns.length
        ? this.$t('CAMPAIGN.WHATSAPP.404')
        : this.$t('CAMPAIGN.WHATSAPP.INBOXES_NOT_FOUND');
    },
  },
  methods: {
    getLabels(ids) {
      return this.labels
        .filter(item => ids.includes(item.id))
        .map(({ title, color }) => {
          return { title, color };
        });
    },
    generateTimestamp(date) {
      const time = new Date(date).getTime() / 1000;
      return Math.floor(time);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';

.table-wrap {
  flex: 1 1;
  height: 100%;
  overflow: hidden;
}

.table-wrap::v-deep {
  // The table must always end exactly where its flex-allocated space ends
  // (height: 100%, not a viewport-relative max-height guess), or ve-table's
  // own scroll container renders taller than what's actually visible and
  // the bottom rows get clipped by `.table-wrap`'s `overflow: hidden` with
  // no way to scroll to them - this reproduced at some zoom levels/screen
  // heights but not others, since a `calc(100vh - Nrem)` guess can only
  // ever match one specific combination of chrome height and zoom.
  .ve-table {
    height: 100%;
    padding-bottom: var(--space-small);
  }

  .row--campaign-block {
    align-items: center;
    display: flex;
    text-align: left;

    .campaign-block {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      margin: 0 var(--space-small);
      max-width: var(--space-giga);

      h6 {
        color: var(--c-500);
        text-transform: capitalize;
        font-size: var(--font-size-small);
      }
    }

    .view-details--button {
      color: var(--b-800);
    }
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
    font-size: var(--font-size-mini) !important;
  }

  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;

    a {
      font-weight: var(--font-weight-medium);
    }
  }

  .ve-table-sort {
    top: -4px;
  }
}

.campaign--loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-big);
}

.button-wrapper {
  justify-content: space-evenly;
  display: flex;
  flex-direction: row;
  min-width: 20rem;
}
</style>
